'use client'

import { useMemo } from 'react'
import { baseDashboardClasses } from '../BaseDashboard'
import type {
  CampaignDomainIdentifier,
  CampaignEntityReferenceIdentifier,
  EntryDomainLink,
  EntryEntityReferenceLink,
} from './campaignTypes'
import type { EntryRow } from './entryTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// The single most valuable thing this feature can show: WHY entries ended up
// in the same campaign. Rule 2 (see campaignTypes.ts) is that an entry
// carrying two identifiers bridges them into one campaign — everything else
// is just "this domain/entity_reference is a campaign on its own". So the
// graph is a plain bipartite diagram: identifier nodes on top, entry nodes
// below, an edge per (entry, identifier) it actually carries (from that
// entry's own entry_domains/entry_entity_references, not the campaign's
// full set). An entry with 2+ edges IS a bridge — it's highlighted because
// it's the literal answer to "why is this domain grouped with that one".
//
// Capped rather than exhaustive: a campaign can have hundreds of member
// entries, almost all single-identifier and visually redundant once one has
// been seen. Every bridging entry is always shown (they're the point);
// non-bridging entries fill remaining slots up to MAX_ENTRY_NODES, newest
// first, with a plain count for the rest.
const MAX_ENTRY_NODES = 30

type IdNode = { key: string; kind: 'domain' | 'entity'; label: string }
type EntryNode = { key: string; label: string; isBridge: boolean; edgeKeys: string[] }

function entryLabel(e: EntryRow): string {
  const raw = e.raw_entries ?? e
  const text = raw.message_content || raw.sender || e.id
  return String(text).slice(0, 28)
}

export function CampaignGraph({
  domains,
  entityReferences,
  entries,
  theme,
  activeIdentifierKey,
  onSelectIdentifier,
}: {
  domains: CampaignDomainIdentifier[]
  entityReferences: CampaignEntityReferenceIdentifier[]
  entries: EntryRow[]
  theme: ProcessedTheme
  activeIdentifierKey: string | null
  onSelectIdentifier: (key: string | null) => void
}) {
  const { isDark, muted, border } = baseDashboardClasses(theme)

  const idNodes: IdNode[] = useMemo(() => [
    ...domains.map((d): IdNode => ({ key: `domain:${d.domain_id}`, kind: 'domain', label: d.domain_url })),
    ...entityReferences.map((e): IdNode => ({ key: `entity:${e.entity_reference_id}`, kind: 'entity', label: `${e.entity} / ${e.reference}` })),
  ], [domains, entityReferences])

  const { shownEntries, hiddenCount } = useMemo(() => {
    const withEdges = entries.map((e) => {
      // The entry's OWN links, not the campaign's — an edge must mean "this
      // entry actually carries this identifier", otherwise every member would
      // appear to touch every identifier and the bridges would be invisible,
      // which is the one thing this graph exists to show.
      const flat = e.processed_entries_flat ?? {}
      const ownDomains: EntryDomainLink[] = flat.entry_domains ?? []
      const ownEntityRefs: EntryEntityReferenceLink[] = flat.entry_entity_references ?? []
      const ownDomainIds = new Set(ownDomains.map((d) => `domain:${d.domain_id}`))
      const ownEntityIds = new Set(ownEntityRefs.map((r) => `entity:${r.entity_reference_id}`))
      const edgeKeys = idNodes.filter((n) => ownDomainIds.has(n.key) || ownEntityIds.has(n.key)).map((n) => n.key)
      return { entry: e, edgeKeys }
    })
    const bridges = withEdges.filter((x) => x.edgeKeys.length > 1)
    const rest = withEdges.filter((x) => x.edgeKeys.length <= 1)
    const remainingSlots = Math.max(0, MAX_ENTRY_NODES - bridges.length)
    const restShown = rest.slice(0, remainingSlots)
    const shown = [...bridges, ...restShown]
    // Keep it roughly chronological (entries already arrive newest-first) —
    // bridges are the point either way, so re-sorting them to the front
    // would bury the timeline the rest already reads as.
    shown.sort((a, b) => entries.indexOf(a.entry) - entries.indexOf(b.entry))
    return {
      shownEntries: shown.map((x): EntryNode => ({ key: x.entry.id, label: entryLabel(x.entry), isBridge: x.edgeKeys.length > 1, edgeKeys: x.edgeKeys })),
      hiddenCount: withEdges.length - shown.length,
    }
  }, [entries, idNodes])

  if (idNodes.length === 0) {
    return <div className={`px-4 py-6 text-center font-mono text-[11px] uppercase tracking-widest ${muted}`}>No identifiers to graph</div>
  }

  const width = Math.max(560, Math.max(idNodes.length, shownEntries.length) * 90)
  const idY = 36
  const entryY = 170
  const idX = (i: number) => (idNodes.length === 1 ? width / 2 : 60 + (i * (width - 120)) / (idNodes.length - 1))
  const entryX = (i: number) => (shownEntries.length === 1 ? width / 2 : 40 + (i * (width - 80)) / (shownEntries.length - 1))
  const idPos = new Map(idNodes.map((n, i) => [n.key, idX(i)]))

  const domainColor = 'var(--signal)'
  const entityColor = 'var(--brand-bcp)'
  const bridgeColor = 'var(--brand-sixt)'
  const dimColor = 'var(--subtle-text)'
  const edgeColor = 'var(--border)'

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={220} className="block">
        {shownEntries.map((en, i) => {
          const ex = entryX(i)
          return en.edgeKeys.map((idKey) => {
            const ix = idPos.get(idKey)
            if (ix == null) return null
            const dim = activeIdentifierKey != null && idKey !== activeIdentifierKey
            return (
              <line
                key={`${en.key}-${idKey}`}
                x1={ix} y1={idY + 10} x2={ex} y2={entryY - 8}
                stroke={dim ? edgeColor : en.isBridge ? bridgeColor : edgeColor}
                strokeWidth={en.isBridge && !dim ? 1.5 : 1}
                opacity={dim ? 0.25 : en.isBridge ? 0.9 : 0.5}
              />
            )
          })
        })}

        {idNodes.map((n, i) => {
          const x = idX(i)
          const active = activeIdentifierKey === n.key
          const color = n.kind === 'domain' ? domainColor : entityColor
          return (
            <g
              key={n.key}
              transform={`translate(${x}, ${idY})`}
              className="cursor-pointer"
              onClick={() => onSelectIdentifier(active ? null : n.key)}
            >
              <circle r={active ? 10 : 8} fill={color} opacity={activeIdentifierKey && !active ? 0.35 : 1} />
              <text
                y={-14}
                textAnchor="middle"
                fontSize={10}
                fontFamily="monospace"
                fill={'var(--foreground)'}
                opacity={activeIdentifierKey && !active ? 0.4 : 1}
              >
                {n.label.length > 22 ? `${n.label.slice(0, 22)}…` : n.label}
              </text>
            </g>
          )
        })}

        {shownEntries.map((en, i) => {
          const x = entryX(i)
          return (
            <g key={en.key} transform={`translate(${x}, ${entryY})`}>
              <circle r={en.isBridge ? 6 : 4} fill={en.isBridge ? bridgeColor : dimColor} />
              {en.isBridge && (
                <text y={22} textAnchor="middle" fontSize={9} fontFamily="monospace" fill={bridgeColor}>
                  bridge
                </text>
              )}
            </g>
          )
        })}
      </svg>
      {hiddenCount > 0 && (
        <p className={`px-1 pb-2 font-mono text-[10px] uppercase tracking-widest ${muted}`}>
          +{hiddenCount} more single-identifier entries not shown
        </p>
      )}
      <p className={`px-1 pb-2 font-mono text-[10px] ${muted}`}>
        <span style={{ color: bridgeColor }}>●</span> orange = a bridge entry (carries 2+ of these identifiers — the literal reason they're one campaign). Click an identifier to trace its entries.
      </p>
    </div>
  )
}
